import { useEffect, useState } from 'react';
import { ContentState, convertToRaw } from 'draft-js';
import { format } from 'date-fns';
import STATES from '../../../constants/states';
import { REDESIGN_CLASSIC_SUNSET } from '../../../constants/redesign';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import SettingsSection from '../SettingsSection';
import SectionDivider from '../SectionDivider';
import { handleUpdateBusiness } from '../../../helpers/businesses';
import { updateBusinessSchema } from '../../../types/businesses';
import useBusinessWebRedesignToggle from '../../../hooks/businesses/useBusinessWebRedesignToggle';
import RedesignRevertDialog from '../RedesignRevertDialog';

interface ShopPolicyState {
  shopPolicyRaw: ReturnType<typeof convertToRaw>;
}

function emptyRaw() {
  return convertToRaw(ContentState.createFromText(''));
}

interface AddressState {
  streetAddress: string;
  streetAddress2?: string;
  city: string;
  state: string;
  zip: string;
}

interface NameState {
  name: string;
}

function BusinessSettings() {
  const [form, setForm] = useState<FormData | null>(null);
  const [redesignEnabled, setRedesignEnabled] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);

  const { user, reloadUser } = useUser<LoadedUser>();
  const {
    canManage: canManageRedesign,
    isSaving: isTogglingRedesign,
    setEnabled: setRedesignEnabledRemote,
  } = useBusinessWebRedesignToggle();

  const isRedesignEligible =
    canManageRedesign &&
    !!user?.business?.businessWebRedesignEligible &&
    !user?.business?.businessWebRedesignLocked;

  useEffect(() => {
    setRedesignEnabled(!!user?.business?.businessWebRedesignEnabled);
  }, [user?.business?.businessWebRedesignEnabled]);

  if (!user?.business) return null;

  const { business } = user;

  const onToggleBusinessWebRedesign = async (enabled: boolean) => {
    if (!enabled) {
      setRevertOpen(true);
      return;
    }
    setRedesignEnabled(true);
    const saved = await setRedesignEnabledRemote(true);
    if (!saved) setRedesignEnabled(false);
  };

  const onConfirmRevert = async (reason?: string) => {
    setRedesignEnabled(false);
    const saved = await setRedesignEnabledRemote(false, reason);
    if (!saved) setRedesignEnabled(true);
  };

  const sunsetCopy = REDESIGN_CLASSIC_SUNSET
    ? `Classic goes away on ${format(REDESIGN_CLASSIC_SUNSET, 'MMMM d')}.`
    : 'Classic will be removed after the redesign rolls out fully.';

  const businessAddress = `${business.streetAddress}, ${
    business.streetAddress2 ? `${business.streetAddress2}, ` : ''
  }${business.city}, ${business.state} ${business.zip}`;

  const initialValues = {
    name: {
      name: business.name || '',
    } as NameState,
    business: {
      streetAddress: business.streetAddress || '',
      streetAddress2: business.streetAddress2 || '',
      city: business.city || '',
      state:
        STATES.find(
          state =>
            state.abbreviation === business.state ||
            state.name === business.state,
        )?.abbreviation || '',
      zip: business.zip || '',
    } as AddressState,
    shopPolicy: {
      shopPolicyRaw: (business.shopPolicyRaw as any) || emptyRaw(),
    } as ShopPolicyState,
  };

  const onSuccess = async () => {
    await reloadUser();
    setForm(null);
  };

  const businessAddressForm: FormData<AddressState> = {
    title: 'Edit business address',
    initialValues: initialValues.business,
    validationSchema: updateBusinessSchema,
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Street address',
          name: 'streetAddress',
          placeholder: '123 Main St.',
        },
      ],
      [
        {
          inputType: 'text',
          label: 'Floor, suite, etc.',
          name: 'streetAddress2',
        },
      ],
      [
        {
          inputType: 'text',
          label: 'City',
          name: 'city',
          placeholder: 'Los Angeles',
        },
      ],
      [
        {
          inputType: 'select',
          label: 'State',
          name: 'state',
          placeholder: 'Select state',
          options: STATES.map(state => ({
            value: state.abbreviation,
            label: state.name,
          })),
        },
        {
          inputType: 'text',
          label: 'Zip',
          name: 'zip',
          placeholder: '90001',
        },
      ],
    ],
    onSubmit: async values => {
      const response = await handleUpdateBusiness(business.id, {
        streetAddress: values.streetAddress ?? undefined,
        streetAddress2: values.streetAddress2 ?? undefined,
        city: values.city ?? undefined,
        state: values.state ?? undefined,
        zip: values.zip ?? undefined,
      });
      if (response) {
        await onSuccess();
      }
    },
  };

  const businessNameForm: FormData<NameState> = {
    title: 'Edit business name',
    validationSchema: updateBusinessSchema,
    initialValues: initialValues.name,
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Business name',
          name: 'name',
          placeholder: 'Tattoo Shop',
        },
      ],
    ],
    onSubmit: async values => {
      const response = await handleUpdateBusiness(business.id, {
        name: values.name ?? undefined,
      });
      if (response) {
        await onSuccess();
      }
    },
  };

  const shopPolicyForm: FormData<ShopPolicyState> = {
    title: 'Edit pre-arrival insructions',
    initialValues: initialValues.shopPolicy,
    inputRows: [
      [
        {
          inputType: 'richtext',
          name: 'shopPolicyRaw',
          label: 'Pre-arrival insructions',
          placeholder:
            'Add any information to show customers before they arrive at the shop. You can use this to show arrival information, any necessary preparation, parking instructions, etc.',
          minHeight: 220,
        },
      ],
    ],
    onSubmit: async values => {
      const response = await handleUpdateBusiness(business.id, {
        shopPolicyRaw: values.shopPolicyRaw ?? undefined,
      });
      if (response) {
        await onSuccess();
      }
    },
  };

  return (
    <>
      {isRedesignEligible && (
        <SettingsSection
          title="Porter design"
          rows={[
            {
              title: redesignEnabled
                ? 'Switch back to classic'
                : 'Try the new Porter design',
              label: redesignEnabled
                ? `You're on the new Porter design. ${sunsetCopy}`
                : 'Preview the redesigned calendar, sidebar, and checkout. You can switch back to classic anytime.',
              action: {
                type: 'switch',
                checked: redesignEnabled,
                disabled: isTogglingRedesign,
                onChange: onToggleBusinessWebRedesign,
              },
            },
          ]}
        />
      )}
      {isRedesignEligible && (
        <RedesignRevertDialog
          open={revertOpen}
          onClose={() => setRevertOpen(false)}
          onConfirm={onConfirmRevert}
          isSaving={isTogglingRedesign}
        />
      )}
      {isRedesignEligible && <SectionDivider />}
      <SettingsSection
        title="Business Information"
        rows={[
          {
            title: 'Business name',
            label: business.name,
            action: {
              type: 'button',
              text: 'Update name',
              onClick: () => setForm(businessNameForm),
            },
          },
          {
            title: 'Business address',
            label: businessAddress,
            action: {
              type: 'button',
              text: 'Update address',
              onClick: () => setForm(businessAddressForm),
            },
          },
          {
            title: 'Pre-arrival insructions',
            label: business.shopPolicyRaw
              ? 'Edit pre-arrival insructions'
              : 'Add pre-arrival insructions',
            tooltip:
              'Add any information to show customers before they arrive at the shop. You can use this to show arrival information, any necessary preparation, parking instructions, etc.',
            action: {
              type: 'button',
              text: business.shopPolicyRaw ? 'Edit' : 'Add',
              onClick: () => setForm(shopPolicyForm),
            },
          },
        ]}
      />
      <FormSmallModal
        open={!!form}
        onClose={() => setForm(null)}
        form={form}
        maxWidth={500}
      />
    </>
  );
}

export default BusinessSettings;
